import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { message } from 'antd';

// API基础配置
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:7070' 
  : window.location.origin;

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        const { response } = error;
        
        if (response) {
          switch (response.status) {
            case 401:
              this.handleUnauthorized(response.data);
              break;
            case 403:
              this.handleForbidden(response.data);
              break;
            case 500:
              message.error('服务器内部错误，请稍后重试');
              break;
            default:
              if (response.data?.message) {
                message.error(response.data.message);
              }
          }
        } else {
          message.error('网络连接失败，请检查网络设置');
        }

        return Promise.reject(error);
      }
    );
  }

  private handleUnauthorized(data: any) {
    if (data.error === 'expired_token') {
      this.refreshToken().catch(() => {
        this.logout();
      });
    } else {
      this.logout();
    }
  }

  private handleForbidden(data: any) {
    if (data.error === 'password_not_initialized') {
      // 跳转到密码初始化页面
      window.location.href = '/auth/initialize-password';
    } else if (data.error === 'otp_not_verified') {
      // 跳转到OTP验证页面
      window.location.href = '/auth/verify-otp';
    } else {
      message.error(data.message || '权限不足');
    }
  }

  private async refreshToken(): Promise<void> {
    try {
      const response = await this.instance.post('/api/v1/auth/refresh');
      const { token, expires_at } = response.data;
      
      this.setToken(token);
      
      // 设置token过期时间
      const expiresDate = new Date(expires_at);
      Cookies.set('token_expires', expiresDate.toISOString());
      
    } catch (error) {
      throw error;
    }
  }

  private logout() {
    this.clearToken();
    window.location.href = '/auth/login';
  }

  public getToken(): string | null {
    return Cookies.get('auth_token') || localStorage.getItem('auth_token');
  }

  public setToken(token: string): void {
    Cookies.set('auth_token', token, { expires: 1 }); // 1天过期
    localStorage.setItem('auth_token', token);
  }

  public clearToken(): void {
    Cookies.remove('auth_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
  }

  // 通用请求方法
  public async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.request(config);
    return response.data;
  }

  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    // 为邮箱相关操作设置更长的超时时间
    const isEmailOperation = url.includes('/profile') || url.includes('/email') || url.includes('/verify');
    const finalConfig = isEmailOperation ? { timeout: 30000, ...config } : config;
    return this.request<T>({ ...finalConfig, method: 'POST', url, data });
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    // 为邮箱相关操作设置更长的超时时间
    const isEmailOperation = url.includes('/profile') || url.includes('/email') || url.includes('/verify');
    const finalConfig = isEmailOperation ? { timeout: 30000, ...config } : config;
    return this.request<T>({ ...finalConfig, method: 'PUT', url, data });
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  public async upload<T = any>(url: string, file: File, data?: any): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (data) {
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });
    }

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

// 创建API服务实例
export const apiService = new ApiService();
export default apiService;